import { useIsMobile } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import type { RefObject } from "react";
import React, { useState } from "react";
import type { SelectorOption } from "@/components/gamesetup/SelectorOptionElem.js";
import { SelectorOptionElem } from "@/components/gamesetup/SelectorOptionElem.js";
import { AnimatePresence, motion } from "framer-motion";


const MENU_CLASSES = "grid w-full grid-cols-1 gap-4 md:grid-cols-2";


const fadeExpandIn = {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: "auto" },
    exit: { opacity: 0, height: 0 },
    style: { overflow: "hidden" },
    transition: {
        ease: "linear" as const,
        duration: 0.2,
    },
};


interface SelectorProps<V> {
    style?: "dropdown" | "flat";
    scrollRef: RefObject<HTMLHeadingElement | null>;
    options: SelectorOption<V>[];
    inlineIcon: boolean;
    selection: V;
    setSelection: (value: V) => void;
}

export function Selector<V>({
    style, scrollRef, options, inlineIcon,
    selection, setSelection,
}: SelectorProps<V>) {
    if (options.length === 0)
        throw new Error("No options provided");

    style ??= "dropdown";
    const isDropdown = (style === "dropdown");

    const isMobile = useIsMobile();

    const [isOpenState, setOpen] = useState<boolean>(false);
    const isOpen = (isOpenState || style === "flat");

    const updateOpen = (open: boolean) => {
        if (isOpen !== open && scrollRef.current) {
            setTimeout(() => {
                scrollRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "nearest",
                });
            });
        }
        setOpen(open);
    };

    const toggleOpen = () => updateOpen(!isOpen);
    const makeSelection = (value: V) => {
        updateOpen(false);
        setSelection(value);
    };

    let selectedOption: SelectorOption<V> | null = null;
    for (const option of options) {
        const { value } = option;
        if (value === selection) {
            selectedOption = option;
        }
    }
    if (selectedOption === null) {
        console.warn("Selection is not in options: " + JSON.stringify(selection));
        selectedOption = options[0];
    }

    const optionElements = [];
    for (const option of options) {
        const { value } = option;

        if (isMobile && isDropdown && value === selection)
            continue;

        optionElements.push(
            <SelectorOptionElem
                key={option.name}
                option={option}
                inlineIcon={inlineIcon}
                onClick={() => makeSelection(value)} />,
        );
    }

    return (
        <div className="block w-full">
            {!isDropdown && (
                <div className={MENU_CLASSES}>
                    {optionElements}
                </div>
            )}

            {isDropdown && (
                <>
                    <SelectorOptionElem
                        className="mb-4"
                        option={selectedOption}
                        mode={isOpen ? "selection-open" : "selection"}
                        inlineIcon={inlineIcon}
                        onClick={() => toggleOpen()} />

                    <AnimatePresence>
                        {isOpen && (
                            <motion.div
                                className={MENU_CLASSES}
                                {...fadeExpandIn}>
                                {optionElements}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}
        </div>
    );
}
